import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ConfirmPopup from '@components/ConfirmPopup'
import ErrorMessage from '@components/ErrorMessage'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { preparePendoCustomListEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoEventType } from '@modules/pendo/types'
import { UpdateManyMap } from '@modules/shortlist/api/shortlist'
import { SHORTLIST_STATUS_MAP } from '@modules/shortlist/constants'
import { Shortlist } from '@modules/shortlist/types'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'

import { ShortlistItemRow } from './ShortlistItemRow'
import { StyledShortlistItemsContainer, StyledShortlistItemsContainerOuter } from './index.styles'
import { EditShortlistsPopupProps } from './types'
import { updateItemInTheMap } from './utils'

const EditShortlistsPopup = (props: EditShortlistsPopupProps) => {
  const { isOpen, onConfirm, shortlists, onCancel, shouldClose, onDelete } = props
  const { hasAccessToMultipleShortlists } = useFeatureFlags()
  const [shortlistIdInEditMode, setShortlistIdInEditMode] = useState<Nullable<Shortlist['id']>>(null)

  const [validationError, setValidationError] = useState('')
  const scrollRef = useRef<Nullable<HTMLDivElement>>(null)

  const { t } = useTranslation()
  const { trackPendoEvent } = usePendoTrackEvent()
  const { user } = useAuthContext()
  const [localShortlistUpdateData, setLocalShortlistUpdateData] = useState<UpdateManyMap>({})

  const handleSetShortlistEditMode = useCallback(
    (value: boolean, shId: Nullable<Shortlist['id']>) => !validationError && setShortlistIdInEditMode(value ? shId : null),
    [validationError],
  )

  const handleDelete = useCallback(
    (id: Shortlist['id']) => {
      const pendoEvent = preparePendoCustomListEvent({
        eventType: 'delete-custom-list',
        customListId: id,
        user,
      })
      trackPendoEvent(PendoEventType.ShortlistEdit, pendoEvent)
      onDelete(id)
    },
    [user, trackPendoEvent, onDelete],
  )

  const handleSwitch = useCallback(
    (sh: Shortlist, newStatusBool: boolean) => {
      setLocalShortlistUpdateData(currStatuses =>
        updateItemInTheMap(currStatuses, {
          status: newStatusBool ? SHORTLIST_STATUS_MAP.ENABLED : SHORTLIST_STATUS_MAP.DISABLED,
          rawShortlist: sh,
        }),
      )
      const pendoEvent = preparePendoCustomListEvent({
        customListId: sh.id,
        eventType: 'edit-custom-list',
        user,
      })
      trackPendoEvent(PendoEventType.ShortlistEdit, pendoEvent)
    },
    [user, trackPendoEvent],
  )

  const handleNameChange = useCallback(
    (sh: Shortlist, newName: string) => {
      if (newName?.length && !newName?.trim()) {
        return
      }

      const { id } = sh

      setLocalShortlistUpdateData(currStatuses =>
        updateItemInTheMap(currStatuses, {
          name: newName,
          rawShortlist: sh,
        }),
      )

      if (!newName) {
        setValidationError('You must add a name')
      } else if (newName && shortlists.some(s => s.name.toLowerCase() === newName?.toLowerCase() && s.id !== id)) {
        setValidationError('Name is already in use')
      } else {
        setValidationError('')
      }
    },
    [shortlists],
  )

  const handleConfirm = useCallback(() => {
    const data = Object.entries(localShortlistUpdateData).reduce(
      (acc, [key, sh]) => ({ ...acc, [key]: { ...sh, name: sh?.name?.trim() } }),
      {},
    )
    const pendoEvent = preparePendoCustomListEvent({
      eventType: 'Confirm',
      user,
    })
    trackPendoEvent(PendoEventType.ShortlistEdit, pendoEvent)
    onConfirm(data)
  }, [localShortlistUpdateData, onConfirm, trackPendoEvent, user])

  const handleCancelWithConfirm = useCallback(() => {
    const proceedWithClose = shouldClose?.(localShortlistUpdateData) ?? true
    const pendoEvent = preparePendoCustomListEvent({
      eventType: 'Cancel',
      user,
    })
    trackPendoEvent(PendoEventType.ShortlistEdit, pendoEvent)
    if (proceedWithClose) {
      onCancel?.()
    }
  }, [localShortlistUpdateData, onCancel, shouldClose, trackPendoEvent, user])

  const displayedShortlists = !hasAccessToMultipleShortlists ? shortlists.slice(0, 1) : shortlists

  return (
    <ConfirmPopup
      isOpen={isOpen}
      width={290}
      title={t(translationKeys.EDIT_CUSTOM_LISTS)}
      text={t(translationKeys.SHOW_ON_CUSTOM_LIST_PAGE)}
      cancelBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
      confirmBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CONFIRM_BTN)}
      onCancel={onCancel}
      onBackdropClick={handleCancelWithConfirm}
      onConfirm={handleConfirm}
      confirmDisabled={!!validationError}
    >
      <StyledShortlistItemsContainerOuter>
        <StyledShortlistItemsContainer ref={scrollRef}>
          {displayedShortlists.map(sh => (
            <ShortlistItemRow
              key={sh.id}
              shortlist={sh}
              shortlistName={localShortlistUpdateData?.[sh.id]?.name || sh.name}
              value={(localShortlistUpdateData?.[sh.id]?.status ?? sh.status) === SHORTLIST_STATUS_MAP.ENABLED}
              onSwitch={handleSwitch}
              onDelete={handleDelete}
              onChange={handleNameChange}
              validationError={validationError}
              isInEditMode={shortlistIdInEditMode === sh.id}
              setIsInEditMode={handleSetShortlistEditMode}
              hasMultipleShortlists={hasAccessToMultipleShortlists}
            />
          ))}
        </StyledShortlistItemsContainer>
      </StyledShortlistItemsContainerOuter>
      <ErrorMessage error={validationError} />
    </ConfirmPopup>
  )
}

export default EditShortlistsPopup
